/**
 * File List Component
 *
 * Displays files referenced by an entity with download/remove actions.
 */

import { Button, List, Space, Typography, Popconfirm, message, Empty, Spin, Tooltip } from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filesApi, type FileRecord } from '@api/files';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface FileListProps {
  entityType: string;
  entityId: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
  if (mimeType === 'application/pdf') return <FilePdfOutlined style={{ color: '#f5222d' }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileWordOutlined style={{ color: '#1890ff' }} />;
  return <FileOutlined />;
}

export default function FileList({ entityType, entityId }: FileListProps) {
  const queryClient = useQueryClient();

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['files', entityType, entityId],
    queryFn: () => filesApi.list(entityType, entityId),
    enabled: !!entityId,
  });

  const removeRefMutation = useMutation({
    mutationFn: (referenceId: string) => filesApi.deleteReference(referenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', entityType, entityId] });
      message.success('File reference removed');
    },
    onError: () => message.error('Failed to remove file'),
  });

  const handleDownload = (file: FileRecord) => {
    const url = filesApi.getDownloadUrl(file.id);
    window.open(url, '_blank');
  };

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>;
  }

  if (files.length === 0) {
    return <Empty description="No files attached" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <List
      size="small"
      dataSource={files}
      renderItem={(file) => (
        <List.Item
          key={file.id}
          actions={[
            <Tooltip title="Download" key="download">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(file)}
              />
            </Tooltip>,
            file.referenceId ? (
              <Popconfirm
                key="remove"
                title="Remove file reference?"
                description="The file won't be deleted, only unlinked."
                onConfirm={() => removeRefMutation.mutate(file.referenceId!)}
                okText="Remove"
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ) : null,
          ].filter(Boolean)}
        >
          <List.Item.Meta
            avatar={getFileIcon(file.mimeType)}
            title={
              <Text style={{ fontSize: 13 }}>{file.originalName}</Text>
            }
            description={
              <Space size={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>{formatSize(file.sizeBytes)}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{file.uploaderName || 'Unknown'}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(file.createdAt).fromNow()}</Text>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );
}
